import { useAdminFlowReview } from "../../hooks/useAdminFlowReview";
import type { AdminFlowReviewProps } from "../../models/admin-flows.models";
import { ADMIN_FLOW_BUTTON, ADMIN_FLOW_PANEL, ADMIN_FLOW_PRIMARY } from "../../utils/admin-flows.constants";
import { orderAdminFlowNodes } from "../../utils/admin-flows.utils";
import AdminFlowConversation from "./AdminFlowConversation";
import AdminFlowRunGraph from "./AdminFlowRunGraph";
import { Provider } from "jotai";
import { useState } from "react";

// Inspect the frozen run and its individual steps; rerunning creates a fresh execution rather than mutating evidence.
const AdminFlowReview = ({ id, token, onEdit, onRunAgain, onStop, busy }: AdminFlowReviewProps) => {
  const { run, error, reload } = useAdminFlowReview(token, id);
  const [selectedNode, setSelectedNode] = useState<string>();
  const [showConversation, setShowConversation] = useState(false);
  const step = run?.steps.find((entry) => entry.nodeId === selectedNode) ?? run?.steps[0];

  if (!id)
    return (
      <section data-test="admin-flow-review-empty" className={`${ADMIN_FLOW_PANEL} p-12 text-center`}>
        <h2 className="font-semibold">Select a run to review</h2>
        <p className="mt-2 text-sm opacity-60">Open a run from History, or select Test flow to start one.</p>
      </section>
    );
  if (error)
    return (
      <div role="alert" className="space-y-3 text-sm text-red-600">
        <p>{error}</p>
        <button data-test="admin-flow-review-retry" className={ADMIN_FLOW_BUTTON} onClick={reload}>
          Reload run
        </button>
      </div>
    );
  if (!run)
    return (
      <p role="status" className="p-8 text-sm opacity-60">
        Loading run…
      </p>
    );

  const orderedSteps = orderAdminFlowNodes(run.flow).flatMap((node) =>
    run.steps.filter((entry) => entry.nodeId === node.id),
  );

  return (
    <section data-test="admin-flow-review" className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{run.flowName} · run review</h2>
          <p className="mt-1 text-xs opacity-60">
            Revision {run.flowRevision} · {new Date(run.startedAt).toLocaleString()} · {run.status.replace(/_/g, " ")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-test="admin-flow-review-edit" className={ADMIN_FLOW_BUTTON} onClick={() => onEdit(run.flow)}>
            Back to builder
          </button>
          <button
            data-test="admin-flow-review-conversation"
            className={ADMIN_FLOW_BUTTON}
            onClick={() => setShowConversation((current) => !current)}
          >
            {showConversation ? "Hide conversation" : "Continue conversation"}
          </button>
          {run.status === "running" ? (
            <button data-test="admin-flow-review-stop" className={ADMIN_FLOW_BUTTON} disabled={!!run.stopRequestedAt} onClick={() => onStop(run.id)}>
              {run.stopRequestedAt ? "Stop requested" : "Stop run"}
            </button>
          ) : (
            <button
              data-test="admin-flow-review-rerun"
              className={ADMIN_FLOW_PRIMARY}
              disabled={busy}
              onClick={() => onRunAgain(run.flow)}
            >
              Run again with fresh data
            </button>
          )}
        </div>
      </header>
      {run.stopRequestedAt && run.status === "running" && (
        <p role="status" data-test="admin-flow-stop-requested" className="rounded bg-[#FFF5DE] p-3 text-sm text-[#725011]">
          Stop requested. The current external call may still finish; its result will not be published.
        </p>
      )}
      {run.error && (
        <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-700">
          {run.error}
        </p>
      )}
      <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className={`${ADMIN_FLOW_PANEL} p-4`}>
          <h3 className="mb-4 font-semibold">Execution timeline</h3>
          <ol className="space-y-2">
            {orderedSteps.map((entry) => (
              <li key={entry.nodeId}>
                <button
                  data-test={`admin-flow-review-step-${entry.nodeId}`}
                  onClick={() => setSelectedNode(entry.nodeId)}
                  className={`w-full rounded border-l-2 p-3 text-left text-sm ${step?.nodeId === entry.nodeId ? "border-green bg-[#E9F4F3] text-[#164b43]" : "border-greyscale-20"}`}
                >
                  <span className="block font-medium">{entry.label}</span>
                  <span className="mt-1 block text-xs opacity-70">{entry.status.replace(/_/g, " ")}</span>
                </button>
              </li>
            ))}
          </ol>
        </aside>
        <div className="min-w-0 space-y-4">
          <AdminFlowRunGraph run={run} onSelect={setSelectedNode} />
          {step && (
            <article className={`${ADMIN_FLOW_PANEL} space-y-4 p-4`} data-test="admin-flow-review-step-details">
              <h3 className="font-semibold">{step.label}</h3>
              {step.error && (
                <p role="alert" className="text-sm text-red-600">
                  {step.error}
                </p>
              )}
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase opacity-60">Input</h4>
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-greyscale-10 p-3 text-xs dark:bg-gray-900">
                  {step.input || "No upstream input"}
                </pre>
              </div>
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase opacity-60">Output</h4>
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded bg-greyscale-10 p-3 text-xs dark:bg-gray-900">
                  {step.output || "No output recorded"}
                </pre>
              </div>
            </article>
          )}
          <article className={`${ADMIN_FLOW_PANEL} p-4`}>
            <h3 className="mb-3 font-semibold">Flow output</h3>
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words text-sm">
              {run.output || "No final output yet."}
            </pre>
          </article>
          {run.messages.length > 0 && (
            <details className={`${ADMIN_FLOW_PANEL} p-4`} data-test="admin-flow-review-messages">
              <summary className="cursor-pointer font-semibold">
                Run conversation ({run.messages.length} messages)
              </summary>
              <div className="mt-4 space-y-3">
                {run.messages.map((entry) => (
                  <article key={entry.id} className="border-greyscale-20 border-l-2 pl-3">
                    <p className="text-xs font-semibold opacity-60">
                      {entry.actor} · {entry.role} · {new Date(entry.createdAt).toLocaleTimeString()}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm">{entry.content}</p>
                  </article>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
      {showConversation && (
        <Provider key={run.id}>
          <AdminFlowConversation run={run} />
        </Provider>
      )}
    </section>
  );
};

export default AdminFlowReview;
